<template>
	<b-container fluid class='report-page'>
		<b-row>
			<b-col cols="2">
				<b-form-group>
					<label>{{ $t('report.start_date') }}</label>									
					<b-form-datepicker 
						v-model="startDate" 
						:locale="locale" 
						size="sm" 
						start-weekday="1" 
						v-bind="dateTranslations || {}"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" />
					</b-form-group>
			</b-col>
			<b-col  cols="2">
				<b-form-group>
					<label>{{ $t('report.end_date') }}</label>									
					<b-form-datepicker 
						v-model="endDate" 
						:locale="locale"
						size="sm" 
						start-weekday="1" 
						v-bind="dateTranslations || {}"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" />
				</b-form-group>
			</b-col>
			<b-col class="btn-v-align">
				<b-button type="submit" class="btn-success btn-report" @click="generateReport()" :disabled="buttonDisabled">
					<b-icon icon="arrow-clockwise" scale="1.2"></b-icon>
					{{ $t('report.generate') }}
				</b-button>
					<b-button class="btn-success btn-report" @click="printReport()" :disabled="buttonDisabled || !generated">
                    <b-icon icon="printer" scale="1.2"></b-icon>
                    {{ $t('report.print') }}
                </b-button>
				<b-button class="btn-success btn-report" @click="generateXLS()" :disabled="buttonDisabled || !generated">
					<b-icon icon="download" scale="1.2"></b-icon>
					{{ $t('report.download') }}
				</b-button>
			</b-col>
       </b-row>
		<b-row>
			<b-col cols='9' class='report-col'>
				<b-table
					striped
					:items="fetchedItems"
					:fields="fields"
					:per-page="perPage"
					:current-page="currentPage"
					:busy="loading">
					<template #table-busy>
						<Loading />
					</template>

					<template v-slot:cell(actions)="row">
                        <b-button size="sm" @click="getDetails(row)" :disabled="row.item.tripsOnDrive == 0">{{ row.detailsShowing ? '-' : '+' }}</b-button>
                    </template>

					<template v-slot:row-details="row"  >
						<div v-if="!row.item.mergedVehicle">
							<Loading />
						</div>
						<div class="subtable" v-else >	
							<div v-for="trip of row.item.mergedVehicle" :key="trip.id" class="subrow">
								<div class="detail_spacer"></div>
								<div class="time"> {{ trip.withVehicle.time }} </div>
								<div class="line"> {{ trip.withVehicle.line }} </div>
								<div class="destination"> {{ trip.withVehicle.dest }} </div>
								<div class="time"> {{ trip.withoutVehicle.time }} </div>
								<div class="line"> {{ trip.withoutVehicle.line }} </div>
								<div class="destination"> {{ trip.withoutVehicle.dest }} </div>
							</div>
						</div>

					</template>

				</b-table>

				<TablePagination
					:total-rows="totalRows"
					:per-page="perPage"
					@pageChange="onPageChange" />
			</b-col>
			<b-col cols="3" > 
				<div class="summary">
					<card>
						<h6> {{ summary.period }} </h6>
						<b-row><b-col cols="8"> {{ $t('trip_report.summary.total') }} </b-col><b-col> {{ summary.total }} </b-col></b-row>
						<b-row><b-col cols="8"> {{ $t('trip_report.summary.ok') }} </b-col><b-col> {{ summary.ok }} </b-col></b-row>
						<b-row><b-col cols="8"> {{ $t('trip_report.summary.error') }} </b-col><b-col> {{ summary.error }}</b-col></b-row>
					</card>
					<circle-chart-card 
						:percentage="this.okPercentage"
						:key="'ok_card'+this.okPercentage"  
						:description="$t('report.trips_on_drive_with_vehicle')" 
						color="green" 
						:height="295" />
					<circle-chart-card 
						:percentage="this.errorPercentage"
						:key="'error_card'+this.errorPercentage"  
						:description="$t('report.trips_without_vehicle')" 
						color="red" 
						:height="295" />
				</div>
			</b-col>
		</b-row>
	</b-container>
</template>

<script>
import TablePagination from '@/components/Misc/TablePagination';
import realtime from '@/store/realtime/realtime'
import moment from 'moment';
import CircleChartCard from 'src/components/UIComponents/Cards/CircleChartCard';

export default {
	components: { TablePagination, CircleChartCard },
	data: function() {
		return {
			perPage: 24,
			currentPage: 1,
            startDate: new moment().format('YYYY-MM-DD'),
			endDate: new moment().format('YYYY-MM-DD'),
			summary: {
				period: "-",
				total: "-",
				ok: "-",
				error: "-"
			},
			fetchedItems: [],
			loading: false,
			generated: false,
            printing : false
		};
	},
	computed: {
		fields() {
            return [
                { key: 'date', label: this.$t('report.date') },
                { key: 'hour', label: this.$t('report.hour'),
                    formatter: function(value) {
						var hourStart = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH');
						if (hourStart.startsWith('Invalid')) 
							return '';
						var hourEnd = +hourStart + 1;
						if (hourEnd == '00')
							hourEnd = '24';
						else if (hourEnd < 10)
							hourEnd = '0' + hourEnd;
						return hourStart + ' - ' + hourEnd;
					}
				},
                { key: 'tripsOnDrive', label: this.$t('report.trips_on_drive') },
                { key: 'tripsWithVehicle', label: this.$t('report.trips_on_drive_with_vehicle') },
                { key: 'tripsWithoutVehicle', label: this.$t('report.trips_without_vehicle') },
				{ key: 'actions', label: '' }   
            ];
        },
		subtable_fields(){
			return [
				{ key: 'withVehicle', label: this.$t('report.trips_on_drive_with_vehicle') },
                { key: 'withoutVehicle', label: this.$t('report.trips_without_vehicle') }
			];
		},
		items() {
			return this.$store.state.realtime.tripsReport.list;
		},
		totalRows() {
			return this.filteredRows ? this.filteredRows : (this.items ? this.items.length : 0);
		},
        locale: function() {
            return this.$t('datepicker.locale');
        },
        dateTranslations: function() {
            return this.$t('datepicker');
        },
       okPercentage(){
			var percentage = 0;
			if(this.summary.total != "-" && this.summary.ok != "-" && this.summary.ok > 0)
				percentage = Math.round((this.summary.ok / this.summary.total) * 100);

			return percentage;
		},
		errorPercentage(){
			var percentage = 0;
			if(this.summary.total != "-" && this.summary.error != "-" && this.summary.error > 0)
				percentage = Math.round((this.summary.error / this.summary.total) * 100);

			return percentage;
		},
		buttonDisabled() {        
			return !this.startDate || !this.endDate 
					|| (moment(this.endDate, "YYYY-MM-DD") > moment(this.startDate, "YYYY-MM-DD").add(7, 'days') 
					|| this.startDate > this.endDate)
					|| this.loading || this.printing;
		}	
	},
	created() {
        if (!this.$store.state.realtime) {
            this.$store.registerModule("realtime", realtime);
		}
		
		window.addEventListener("afterprint", this.afterprint);
	},
	beforeDestroy(){
        window.removeEventListener("afterprint", this.afterprint);
    },
	methods: {
        generateReport() {
			this.loading = true;
			this.resetSummary();

			var timeRange = {
                'startDate': this.startDate,
                'endDate': this.endDate
            }
			
			var tripsWithVehicle = 0;
			var tripsWithoutVehicle = 0;
			var tripsOnDrive = 0;

			this.$store.dispatch('realtime/getTripsReport', timeRange).then(response => {
				this.fetchedItems = response.data;
				for (var hourSummary of response.data) {
					tripsWithVehicle += hourSummary.tripsWithVehicle;
					tripsWithoutVehicle += hourSummary.tripsWithoutVehicle;
					tripsOnDrive += hourSummary.tripsOnDrive;
				}
			})
			.finally(() => { 
				this.loading = false;
				this.generated = true;

				var startTime = new moment(this.startDate, 'YYYY-MM-DD');
				var endTime2 = new moment(this.endDate, 'YYYY-MM-DD');
				this.summary.period = startTime.format("DD.MM.YYYY") + " - " + endTime2.format("DD.MM.YYYY");

				if (tripsOnDrive > 0) {
					this.summary.total = tripsOnDrive;
					this.summary.ok = tripsWithVehicle;
					this.summary.error = tripsWithoutVehicle;
				}
			});
		},
        generateXLS() {
            this.loading = true;

            var params = {
                'startDate': this.startDate,
                'endDate': this.endDate,
                'header1': this.$t('report.date'),
                'header2': this.$t('report.hour'),
                'header3': this.$t('report.departure_time'),
                'header4': this.$t('report.line'),
                'header5': this.$t('report.destination'),
                'header6': this.$t('report.vehicle_assigned')
            };
            this.$store.dispatch('realtime/getTripsReportXLS', params)
                .finally(() => { this.loading = false; });
        },
        formatTime(time, format) {
            var formatted = new moment(time, 'YYYY-MM-DD[T]HH:mm:ssZZ').format(format);
            if (formatted.startsWith('Invalid')) {
                return '';
            } else
				return formatted;
        },
		getDetails(row){
			row.toggleDetails();
			if(row.detailsShowing) return;
			let item = row.item;
            var startTime = new moment(item.hour, 'YYYY-MM-DD[T]HH:mm');
            var timeRange = {
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': startTime.format('YYYY-MM-DD[T]HH:[59:59]')	
			}
			this.$store.dispatch('realtime/getTrips' , timeRange).then( response => {
			
				let withVehicle = [];
				let withoutVehicle = [];
				let mergedList = [];
				
				for( let trip of response.data){
					if(trip.vehicleLabel != ""){
						withVehicle.push(trip);
					}else{
						withoutVehicle.push(trip);
					}
				}

				withVehicle.sort( (a,b) => (Number(a.startTime) > Number(b.startTime)) ? 1 : ((Number(b.startTime) > Number(a.startTime)) ? -1 : 0) );
				withoutVehicle.sort( (a,b) => (Number(a.startTime) > Number(b.startTime)) ? 1 : ((Number(b.startTime) > Number(a.startTime)) ? -1 : 0) );

				for(let trip of withVehicle){
					let mergedTrip = {};
					mergedTrip.id = mergedList.length;
					let trippi = {};
					trippi.time = this.formatTime(trip.startTime,'HH:mm');
					//trippi.line = trip.lineId;
                    trippi.line = trip.lineNumber != null ? trip.lineNumber : "";
					trippi.dest = trip.destination;
					mergedTrip.withVehicle = trippi;
					mergedTrip.withoutVehicle = {};
					mergedList.push(mergedTrip)
				}

				for(let [index, trip] of withoutVehicle.entries()){
					let trippi = {};
					trippi.time = this.formatTime(trip.startTime,'HH:mm');
					//trippi.line = trip.lineId;
                    trippi.line = trip.lineNumber != null ? trip.lineNumber : "";
					trippi.dest = trip.destination;

					if(mergedList[index]){
						mergedList[index].withoutVehicle = trippi;
					}
					else{
						let mergedTrip = {};
						mergedTrip.id = mergedList.length;
						mergedTrip.withVehicle = {};
						mergedTrip.withoutVehicle = trippi;
						mergedList.push(mergedTrip)
					}
				}

				let index = this.fetchedItems.findIndex( fetchedItem => fetchedItem.hour == item.hour );
				let newObject = this.fetchedItems[index];
				newObject.mergedVehicle = mergedList;
				this.$set(this.fetchedItems, index, newObject)
				//this.fetchedItems.filter( fetchedItem => fetchedItem.hour == item.hour)[0].mergedVehicle = mergedList;
            });
		},
		onFiltered(filteredItems) {
			this.filteredRows = filteredItems.length;
			this.currentPage = 1;
		},
		onPageChange(page) {
			this.currentPage = page;
		},
		resetSummary(){
			this.summary.period = "-";
			this.summary.total = "-";
			this.summary.ok = "-";
			this.summary.error = "-";
		},
		printReport(){
            this.printing = true;
            this.perPage = this.items.length
            // timeout needed because table takes some time to render
            setTimeout(() => { window.print(); }, 10);   
        },
        afterprint(){
            this.perPage = 24;
            this.printing = false;
        }
	}
};
</script>

<style scoped lang="scss">

.subtable {
  
	.header-row{
		padding-right:4%;
		padding-left:3%;
		display: flex;
		flex-direction: row;
	}
	
	.subrow {
		padding-right:4%;
		padding-left:3%;
		display: flex;
		flex-direction: row;
		font-size: 13px;
		font-weight: bold;
	}

	.time {
		flex: 1;
		margin-right:-110px;
	}
	.destination {
		flex: 1;
	}
	.line {
		flex: 1;
		margin-right:-130px;
	}
	.detail_spacer {
		width: 43%;
  }
}

</style>